'use client'

import { forwardRef, ReactNode, useState, useRef, useEffect } from 'react'
import { 
  Eye, 
  EyeOff, 
  AlertCircle, 
  CheckCircle, 
  Info, 
  Search,
  X
} from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentShadows,
  typographyV3 
} from '@birhaus/tokens/v3'
import { cn } from '@birhaus/types/utils'

/**
 * BIRHAUS Input V3 - Radical Minimalist Design
 * 
 * Revolutionary input component with:
 * - Floating labels with smooth animations
 * - Radical border radius (20px default)
 * - Glass morphism background options
 * - Enhanced focus states with generous rings
 * - Generous padding for touch targets
 * - Spanish-first labeling
 * - Advanced validation states
 * - Performance optimized animations
 */


export interface BirhausInputV3Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'autoComplete' | 'size'> {
  // BIRHAUS Principle #7: Spanish-first labeling
  label?: string
  labelEs?: string
  labelEn?: string
  
  placeholder?: string
  placeholderEs?: string
  placeholderEn?: string
  
  // Helper text
  helper?: string
  helperEs?: string
  helperEn?: string
  
  // V3 Design System - Appearance
  variant?: 'default' | 'glass' | 'frosted' | 'outline' | 'minimal'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  radius?: keyof typeof borderRadiusV3
  
  // V3 Enhanced states
  state?: 'default' | 'success' | 'warning' | 'error' | 'loading'
  
  // Input types with enhanced features
  type?: 'text' | 'email' | 'password' | 'search' | 'number' | 'tel' | 'url'
  
  // Icons and actions
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  clearable?: boolean
  onClear?: () => void
  
  // Floating label behavior
  floatingLabel?: boolean
  alwaysFloatLabel?: boolean
  
  // V3 Visual effects
  glow?: boolean
  pulse?: boolean
  
  // Validation
  error?: string
  errorEs?: string
  errorEn?: string
  success?: string
  successEs?: string
  successEn?: string
  
  // Enhanced features
  autoComplete?: boolean
  spellCheck?: boolean
  
  // Performance tracking
  trackPerformance?: boolean
  
  // Container props
  containerClassName?: string
  labelClassName?: string
  helperClassName?: string
}

export const BirhausInputV3 = forwardRef<HTMLInputElement, BirhausInputV3Props>(({
  // Content (Spanish-first)
  label,
  labelEs,
  labelEn,
  placeholder,
  placeholderEs,
  placeholderEn,
  helper,
  helperEs,
  helperEn,
  
  // V3 appearance
  variant = 'default',
  size = 'md',
  radius = 'md',
  
  // States
  state = 'default',
  
  // Type
  type = 'text',
  
  // Icons and actions
  leftIcon,
  rightIcon,
  clearable = false,
  onClear,
  
  // Floating labels
  floatingLabel = true,
  alwaysFloatLabel = false,
  
  // V3 effects
  glow = false,
  pulse = false,
  
  // Validation
  error,
  errorEs,
  errorEn,
  success,
  successEs,
  successEn,
  
  // Features
  autoComplete = true,
  spellCheck = true,
  
  // Performance
  trackPerformance = false,
  
  // Styling
  containerClassName,
  labelClassName,
  helperClassName,
  className,
  
  // Standard props
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled,
  required,
  ...props
}, ref) => {
  
  // State management
  const [isFocused, setIsFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [hasValue, setHasValue] = useState(Boolean(value || defaultValue))
  
  const inputRef = useRef<HTMLInputElement>(null)
  const performanceStartRef = useRef<number>()
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayLabel = resolveText(labelEs, labelEn, label)
  const displayPlaceholder = resolveText(placeholderEs, placeholderEn, placeholder)
  const displayHelper = resolveText(helperEs, helperEn, helper)
  const displayError = resolveText(errorEs, errorEn, error)
  const displaySuccess = resolveText(successEs, successEn, success)
  
  // Determine current state
  const currentState = displayError ? 'error' : displaySuccess ? 'success' : state
  
  // Check if label should float
  const shouldFloatLabel = alwaysFloatLabel || isFocused || hasValue || Boolean(displayPlaceholder)
  
  // V3 Size classes with generous dimensions
  const sizeClasses = {
    sm: 'h-12 px-4 text-sm', // 32px -> 48px height
    md: 'h-14 px-6 text-base', // 40px -> 56px height  
    lg: 'h-16 px-8 text-lg', // 44px -> 64px height
    xl: 'h-20 px-10 text-xl', // 48px -> 80px height
  }
  
  // V3 Variant classes with radical design
  const variantClasses = {
    default: `
      bg-white border-2 border-gray-200
      hover:border-gray-300
      focus:border-blue-500 focus:bg-white
    `,
    
    glass: `
      bg-white/50 backdrop-blur-md border-2 border-white/30
      hover:bg-white/60 hover:border-white/40
      focus:bg-white/70 focus:border-blue-400/50
    `,
    
    frosted: `
      bg-white/70 backdrop-blur-lg border-2 border-white/40
      hover:bg-white/80 hover:border-white/50
      focus:bg-white/90 focus:border-blue-400/60
    `,
    
    outline: `
      bg-transparent border-2 border-gray-300
      hover:border-gray-400 hover:bg-gray-50/30
      focus:border-blue-500 focus:bg-white/50
    `,
    
    minimal: `
      bg-gray-50/50 border-2 border-transparent
      hover:bg-gray-100/50
      focus:bg-white focus:border-blue-500
    `,
  }
  
  // State styling
  const stateClasses = {
    default: '',
    success: 'border-green-400 focus:border-green-500 focus:ring-green-400/20',
    warning: 'border-yellow-400 focus:border-yellow-500 focus:ring-yellow-400/20',
    error: 'border-red-400 focus:border-red-500 focus:ring-red-400/20',
    loading: 'border-blue-400 focus:border-blue-500 focus:ring-blue-400/20',
  }
  
  const stateIcons = {
    default: null,
    success: <CheckCircle className="h-5 w-5 text-green-500" />,
    warning: <AlertCircle className="h-5 w-5 text-yellow-500" />,
    error: <AlertCircle className="h-5 w-5 text-red-500" />,
    loading: <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />,
  }
  
  // Event handlers
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    if (trackPerformance) {
      performanceStartRef.current = performance.now()
    }
    onFocus?.(event)
  }
  
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    if (trackPerformance && performanceStartRef.current) {
      const duration = performance.now() - performanceStartRef.current
      console.log(`[BIRHAUS v3] Input focus took ${duration.toFixed(2)}ms`)
    }
    onBlur?.(event)
  }
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setHasValue(Boolean(newValue))
    onChange?.(event)
  }
  
  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      setHasValue(false)
      inputRef.current.focus()
      onClear?.()
      
      // Create synthetic change event
      const event = new Event('input', { bubbles: true })
      inputRef.current.dispatchEvent(event)
    }
  }
  
  const handlePasswordToggle = () => {
    setShowPassword(!showPassword)
  }
  
  // Determine input type
  const inputType = type === 'password' ? (showPassword ? 'text' : 'password') : type
  
  // Build base input classes
  const baseInputClasses = cn(
    // Base styles with v3 principles
    'w-full transition-all duration-300 ease-out',
    'focus:outline-none focus:ring-4 focus:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-50',
    'transform-gpu', // GPU acceleration
    
    // Size with generous padding
    sizeClasses[size],
    
    // Border radius - radical curves
    `rounded-[${borderRadiusV3[radius]}]`,
    
    // Variant styling
    variantClasses[variant],
    
    // State styling
    currentState !== 'default' && stateClasses[currentState],
    
    // Focus ring
    currentState === 'default' && 'focus:ring-blue-400/20',
    
    // Visual effects
    glow && 'shadow-[0_0_20px_-5px_rgba(31,111,235,0.15)]',
    pulse && 'animate-pulse',
    
    // Padding adjustments for icons
    leftIcon && 'pl-12',
    (rightIcon || clearable || type === 'password' || currentState !== 'default') && 'pr-12',
    
    className
  )
  
  // Container classes
  const containerClasses = cn(
    'relative group',
    containerClassName
  )
  
  // Floating label classes
  const labelClasses = cn(
    'absolute left-6 transition-all duration-300 ease-out pointer-events-none',
    'text-gray-600 font-medium',
    'transform-gpu', // GPU acceleration
    
    // Floating behavior
    shouldFloatLabel 
      ? 'top-2 text-xs scale-90 -translate-y-1 text-blue-600'
      : `top-1/2 -translate-y-1/2 text-base`,
    
    // Focus state
    isFocused && shouldFloatLabel && 'text-blue-600',
    
    // State colors when floating
    shouldFloatLabel && currentState === 'success' && 'text-green-600',
    shouldFloatLabel && currentState === 'warning' && 'text-yellow-600',
    shouldFloatLabel && currentState === 'error' && 'text-red-600',
    
    // Required indicator
    required && "after:content-['*'] after:ml-1 after:text-red-500",
    
    labelClassName
  )
  
  return (
    <div className={containerClasses}>
      {/* Non-floating label */}
      {displayLabel && !floatingLabel && (
        <label 
          htmlFor={props.id} 
          className={cn(
            'block text-sm font-medium text-gray-700 mb-3',
            required && "after:content-['*'] after:ml-1 after:text-red-500",
            labelClassName
          )}
        >
          {displayLabel}
        </label>
      )}
      
      {/* Input container */}
      <div className="relative">
        {/* Left icon */}
        {leftIcon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
            {leftIcon}
          </div>
        )}
        
        {/* Search icon for search inputs */}
        {type === 'search' && !leftIcon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
            <Search className="h-5 w-5" />
          </div>
        )}
        
        {/* Main input */}
        <input
          ref={ref || inputRef}
          type={inputType}
          className={baseInputClasses}
          placeholder={floatingLabel ? undefined : displayPlaceholder}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete ? 'on' : 'off'}
          spellCheck={spellCheck}
          data-birhaus-v3="input"
          data-variant={variant}
          data-size={size}
          data-state={currentState}
          {...props}
        />
        
        {/* Floating label */}
        {displayLabel && floatingLabel && (
          <label 
            htmlFor={props.id}
            className={labelClasses}
          >
            {displayLabel}
          </label>
        )}
        
        {/* Right side icons/actions */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {/* State icon */}
          {currentState !== 'default' && stateIcons[currentState]}
          
          {/* Clear button */}
          {clearable && hasValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'text-gray-400 hover:text-gray-600',
                'transition-colors duration-200',
                'focus:outline-none focus:text-gray-600'
              )}
              aria-label="Limpiar campo"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          
          {/* Password visibility toggle */}
          {type === 'password' && (
            <button
              type="button"
              onClick={handlePasswordToggle}
              className={cn(
                'text-gray-400 hover:text-gray-600',
                'transition-colors duration-200',
                'focus:outline-none focus:text-gray-600'
              )}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          )}
          
          {/* Custom right icon */}
          {rightIcon && (
            <div className="text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>
      </div>
      
      {/* Helper text, success, or error message */}
      <div className="mt-3 min-h-[1.25rem]">
        {displayError && (
          <p className={cn(
            'text-sm text-red-600 flex items-center gap-2',
            'animate-in slide-in-from-top-1 duration-200',
            helperClassName
          )}>
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {displayError}
          </p>
        )}
        
        {displaySuccess && !displayError && (
          <p className={cn(
            'text-sm text-green-600 flex items-center gap-2',
            'animate-in slide-in-from-top-1 duration-200',
            helperClassName
          )}>
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            {displaySuccess}
          </p>
        )}
        
        {displayHelper && !displayError && !displaySuccess && (
          <p className={cn(
            'text-sm text-gray-500 flex items-start gap-2',
            helperClassName
          )}>
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {displayHelper}
          </p>
        )}
      </div>
    </div>
  )
})

BirhausInputV3.displayName = 'BirhausInputV3'

// Export hook for managing v3 input state
export function useBirhausInputV3(initialValue: string = '') {
  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isValid, setIsValid] = useState(false)
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setValue(newValue)
    
    // Clear error when user starts typing
    if (error) {
      setError(null)
    }
  }
  
  const handleFocus = () => setIsFocused(true)
  const handleBlur = () => setIsFocused(false)
  
  const validate = (validator: (value: string) => string | null) => {
    const validationError = validator(value)
    setError(validationError)
    setIsValid(!validationError)
    return !validationError
  }
  
  const reset = () => {
    setValue(initialValue)
    setError(null)
    setSuccess(null)
    setIsValid(false)
  }
  
  return {
    value,
    error,
    success,
    isFocused,
    isValid,
    setValue,
    setError,
    setSuccess,
    setIsValid,
    handleChange,
    handleFocus,
    handleBlur,
    validate,
    reset,
  }
}

// Validation utilities
export const birhausInputValidators = {
  required: (message = 'Este campo es requerido') => (value: string) => {
    return value.trim() ? null : message
  },
  
  email: (message = 'Ingrese un email válido') => (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value) ? null : message
  },
  
  minLength: (min: number, message?: string) => (value: string) => {
    const msg = message || `Debe tener al menos ${min} caracteres`
    return value.length >= min ? null : msg
  },
  
  maxLength: (max: number, message?: string) => (value: string) => {
    const msg = message || `Debe tener máximo ${max} caracteres`
    return value.length <= max ? null : msg
  },
  
  pattern: (regex: RegExp, message = 'Formato inválido') => (value: string) => {
    return regex.test(value) ? null : message
  },
}